import { Buffer } from 'buffer'
import type { Api as ApiNamespace, TelegramClient as TelegramClientType } from 'telegram'

import type {
  AuthUser,
  ChatPeerType,
  ChatSummary,
  MessageRecord,
  SearchResultRecord,
  SessionStatus,
  SyncTaskStatus,
  UserMessageSearchStatus,
} from '../../shared/types'

const SESSION_KEY = 'tg-search-lite-session'
const ENV_API_ID = Number(import.meta.env.VITE_TELEGRAM_API_ID || 0)
const ENV_API_HASH = String(import.meta.env.VITE_TELEGRAM_API_HASH || '').trim()

interface RuntimeApiConfig {
  apiId: number
  apiHash: string
}

interface PersistedSessionState {
  apiId: number
  apiHash: string
  phone: string
  session: string
}

interface PendingLoginState {
  client: TelegramClientType
  apiId: number
  apiHash: string
  phone: string
  step: 'code' | 'password'
  codeDeferred: ReturnType<typeof createDeferred<string>>
  passwordDeferred: ReturnType<typeof createDeferred<string>>
  nextStepDeferred: ReturnType<typeof createDeferred<'password'>>
  resultDeferred: ReturnType<typeof createDeferred<SessionStatus>>
}

interface SyncCancellationState {
  taskId: string
  cancelled: boolean
}

interface ResolvedUserLookup {
  inputUser: any
  senderId: string
  displayName: string
}

let client: TelegramClientType | null = null
let pending: PendingLoginState | null = null
let activeSyncCancellation: SyncCancellationState | null = null
let activeUserSearchCancellation: SyncCancellationState | null = null
let runtimeApiConfig: RuntimeApiConfig = {
  apiId: ENV_API_ID,
  apiHash: ENV_API_HASH,
}
let runtimeApiConfigPromise: Promise<RuntimeApiConfig> | null = null
let telegramRuntimePromise: Promise<{
  Api: typeof import('telegram').Api
  TelegramClient: typeof import('telegram').TelegramClient
  StringSession: new (session?: string) => { save(): unknown }
  ConnectionTCPObfuscated: typeof import('telegram/network').ConnectionTCPObfuscated
  getInputUser: typeof import('telegram/Utils.js').getInputUser
  strippedPhotoToJpg: typeof import('telegram/Utils.js').strippedPhotoToJpg
}> | null = null

function createDeferred<T>() {
  let resolve!: (value: T) => void
  let reject!: (reason?: unknown) => void
  const promise = new Promise<T>((res, rej) => {
    resolve = res
    reject = rej
  })

  return { promise, resolve, reject }
}

async function loadRuntimeApiConfig() {
  if (runtimeApiConfig.apiId && runtimeApiConfig.apiHash) {
    return runtimeApiConfig
  }

  if (!runtimeApiConfigPromise) {
    runtimeApiConfigPromise = (async () => {
      try {
        const response = await fetch(`${import.meta.env.BASE_URL}telegram-api.example.json`, {
          cache: 'no-store',
        })
        if (!response.ok) {
          return runtimeApiConfig
        }

        const payload = await response.json() as Partial<{ apiId: number | string, apiHash: string }>
        const apiId = Number(payload.apiId || 0)
        const apiHash = String(payload.apiHash || '').trim()
        if (apiId && apiHash) {
          runtimeApiConfig = { apiId, apiHash }
        }
      }
      catch {
        // Ignore fetch failures and fall back to Vite env values.
      }

      return runtimeApiConfig
    })()
  }

  return await runtimeApiConfigPromise
}

function readPersistedState(): PersistedSessionState | null {
  const raw = window.localStorage.getItem(SESSION_KEY)
  if (!raw) {
    return null
  }

  try {
    return JSON.parse(raw) as PersistedSessionState
  }
  catch {
    return null
  }
}

function writePersistedState(state: PersistedSessionState | null) {
  if (!state) {
    window.localStorage.removeItem(SESSION_KEY)
    return
  }
  window.localStorage.setItem(SESSION_KEY, JSON.stringify(state))
}

async function loadTelegramRuntime() {
  if (!telegramRuntimePromise) {
    telegramRuntimePromise = Promise.all([
      import('telegram'),
      import('telegram/sessions/StringSession.js'),
      import('telegram/network/index.js'),
      import('telegram/Utils.js'),
    ]).then(([telegramModule, sessionModule, networkModule, utilsModule]) => ({
      Api: telegramModule.Api,
      TelegramClient: telegramModule.TelegramClient,
      StringSession: sessionModule.StringSession,
      ConnectionTCPObfuscated: networkModule.ConnectionTCPObfuscated,
      getInputUser: utilsModule.getInputUser,
      strippedPhotoToJpg: utilsModule.strippedPhotoToJpg,
    }))
  }

  return telegramRuntimePromise
}

function displayUser(user: ApiNamespace.User): AuthUser {
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim() || user.username || user.phone || 'Telegram User'
  return {
    id: user.id.toString(),
    name,
    username: user.username || undefined,
    phone: user.phone || undefined,
  }
}

function peerKey(peer: any) {
  if (!peer) {
    return undefined
  }
  if ('userId' in peer) {
    return `user:${peer.userId.toString()}`
  }
  if ('channelId' in peer) {
    return `channel:${peer.channelId.toString()}`
  }
  if ('chatId' in peer) {
    return `chat:${peer.chatId.toString()}`
  }
  return undefined
}

function buildChatId(peerType: ChatPeerType, peerId: string) {
  return `${peerType}:${peerId}`
}

function normalizeUserLookup(raw: string) {
  return raw
    .trim()
    .replace(/^https?:\/\/(?:www\.)?(?:t\.me|telegram\.(?:me|dog))\//i, '')
    .replace(/^@/, '')
    .replace(/\/+$/, '')
}

function buildSyncCancelledError() {
  const error = new Error('已取消同步')
  ;(error as Error & { code?: string }).code = 'SYNC_CANCELLED'
  return error
}

function buildUserSearchCancelledError() {
  const error = new Error('已取消按用户筛选')
  ;(error as Error & { code?: string }).code = 'USER_SEARCH_CANCELLED'
  return error
}

function formatTakeoutAvailableTime(delaySeconds: number) {
  const targetTs = Date.now() + delaySeconds * 1000
  const utcLabel = new Intl.DateTimeFormat('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZone: 'UTC',
  }).format(targetTs)

  const localLabel = new Intl.DateTimeFormat('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(targetTs)

  return {
    utcLabel,
    localLabel,
  }
}

function normalizeSyncError(error: any) {
  const rawMessage = String(error?.errorMessage || error?.message || '').trim()
  const delayMatch = rawMessage.match(/TAKEOUT_INIT_DELAY_(\d+)/)
  if (!delayMatch) {
    return error
  }

  const delaySeconds = Number(delayMatch[1] || 0)
  const { utcLabel, localLabel } = formatTakeoutAvailableTime(delaySeconds)
  const nextError = new Error(
    `首次导出数据前，Telegram 需要你先确认这次 Data Export 请求。\n` +
    `请打开 Telegram，找到类似 “Data export request” 的官方安全通知消息，然后在手机端点击 Allow 或 Accepted。\n` +
    `如果你当前没有手机端已登录账号，就需要等待冷却结束后，再用同一台设备重新发起同步。\n` +
    `预计可再次尝试时间：UTC ${utcLabel}；本地时间 ${localLabel}。`,
  ) as Error & { code?: string, errorMessage?: string }

  nextError.code = error?.code
  nextError.errorMessage = rawMessage
  return nextError
}

function isHistoryMessage(message: any) {
  if (!message || typeof message !== 'object') {
    return false
  }

  const className = typeof message.className === 'string' ? message.className : ''
  if (className === 'Message' || className === 'MessageService') {
    return true
  }

  return 'id' in message && 'date' in message
}

function getMessageDateTs(message: any) {
  const raw = message?.date
  if (typeof raw === 'number') {
    return raw * 1000
  }
  if (typeof raw === 'bigint') {
    return Number(raw) * 1000
  }
  if (raw instanceof Date) {
    return raw.getTime()
  }
  if (typeof raw === 'string') {
    const parsed = Date.parse(raw)
    if (!Number.isNaN(parsed)) {
      return parsed
    }
  }
  return 0
}

function inferImageMimeType(bytes: Uint8Array) {
  if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
    return 'image/jpeg'
  }
  if (
    bytes.length >= 8
    && bytes[0] === 0x89
    && bytes[1] === 0x50
    && bytes[2] === 0x4e
    && bytes[3] === 0x47
    && bytes[4] === 0x0d
    && bytes[5] === 0x0a
    && bytes[6] === 0x1a
    && bytes[7] === 0x0a
  ) {
    return 'image/png'
  }
  if (
    bytes.length >= 12
    && bytes[0] === 0x52
    && bytes[1] === 0x49
    && bytes[2] === 0x46
    && bytes[3] === 0x46
    && bytes[8] === 0x57
    && bytes[9] === 0x45
    && bytes[10] === 0x42
    && bytes[11] === 0x50
  ) {
    return 'image/webp'
  }
  if (
    bytes.length >= 6
    && bytes[0] === 0x47
    && bytes[1] === 0x49
    && bytes[2] === 0x46
    && bytes[3] === 0x38
  ) {
    return 'image/gif'
  }
  return 'image/jpeg'
}

function toDataUrl(bytesLike: Buffer | Uint8Array | ArrayBuffer) {
  const buffer = Buffer.isBuffer(bytesLike)
    ? bytesLike
    : bytesLike instanceof ArrayBuffer
      ? Buffer.from(new Uint8Array(bytesLike))
      : Buffer.from(bytesLike)

  if (buffer.length === 0) {
    return undefined
  }

  const mimeType = inferImageMimeType(buffer)
  return `data:${mimeType};base64,${buffer.toString('base64')}`
}

async function extractPeerMeta(inputEntity: ApiNamespace.TypeInputPeer): Promise<{ peerType: ChatPeerType, peerId: string, accessHash?: string } | null> {
  const { Api } = await loadTelegramRuntime()

  if (inputEntity instanceof Api.InputPeerSelf) {
    return { peerType: 'self', peerId: 'self' }
  }
  if (inputEntity instanceof Api.InputPeerUser) {
    return {
      peerType: 'user',
      peerId: inputEntity.userId.toString(),
      accessHash: inputEntity.accessHash?.toString(),
    }
  }
  if (inputEntity instanceof Api.InputPeerChat) {
    return {
      peerType: 'chat',
      peerId: inputEntity.chatId.toString(),
    }
  }
  if (inputEntity instanceof Api.InputPeerChannel) {
    return {
      peerType: 'channel',
      peerId: inputEntity.channelId.toString(),
      accessHash: inputEntity.accessHash?.toString(),
    }
  }
  return null
}

async function makeInputPeer(chat: ChatSummary & { peerId: string, accessHash?: string }) {
  const { Api } = await loadTelegramRuntime()

  if (chat.peerType === 'self') {
    return new Api.InputPeerSelf()
  }
  if (chat.peerType === 'chat') {
    return new Api.InputPeerChat({ chatId: BigInt(chat.peerId) as any })
  }
  if (chat.peerType === 'user') {
    return new Api.InputPeerUser({
      userId: BigInt(chat.peerId) as any,
      accessHash: BigInt(chat.accessHash ?? '0') as any,
    })
  }
  return new Api.InputPeerChannel({
    channelId: BigInt(chat.peerId) as any,
    accessHash: BigInt(chat.accessHash ?? '0') as any,
  })
}

function describeResolvedUser(entity: any, fallback: string) {
  if (entity?.className === 'User') {
    return displayUser(entity).name
  }
  return fallback
}

async function resolveUserLookup(current: TelegramClientType, raw: string): Promise<ResolvedUserLookup> {
  const normalized = normalizeUserLookup(raw)
  if (!normalized) {
    throw new Error('先输入要筛选的用户 id 或 username')
  }

  const { Api, getInputUser } = await loadTelegramRuntime()

  if (/^(me|self)$/i.test(normalized)) {
    const me = await current.getMe()
    return {
      inputUser: new Api.InputUserSelf(),
      senderId: `user:${me.id.toString()}`,
      displayName: displayUser(me).name,
    }
  }

  const attempts: any[] = []
  if (/^\d+$/.test(normalized)) {
    attempts.push(normalized)
    const safeNumber = Number(normalized)
    if (Number.isSafeInteger(safeNumber)) {
      attempts.push(safeNumber)
    }
    attempts.push(new Api.PeerUser({ userId: BigInt(normalized) as any }))
  }
  else {
    attempts.push(normalized)
  }

  let inputEntity: any
  let entity: any
  let lastError: unknown
  for (const candidate of attempts) {
    try {
      inputEntity = await current.getInputEntity(candidate)
      try {
        entity = await current.getEntity(candidate)
      }
      catch {
        entity = await current.getEntity(inputEntity)
      }
      break
    }
    catch (error) {
      lastError = error
    }
  }

  if (!inputEntity) {
    throw new Error(
      lastError instanceof Error && lastError.message
        ? `无法解析这个用户：${lastError.message}`
        : '无法解析这个用户。请确认这个 user id 或 username 对当前账号可见。',
    )
  }

  const inputUser = getInputUser(inputEntity)
  const senderId = entity?.id ? `user:${entity.id.toString()}` : /^\d+$/.test(normalized) ? `user:${normalized}` : undefined
  if (!senderId) {
    throw new Error('这个实体不是普通用户账号，不能按发送者筛消息')
  }

  return {
    inputUser,
    senderId,
    displayName: describeResolvedUser(entity, normalized),
  }
}

function readMessageText(message: any) {
  if (typeof message?.message === 'string') {
    return message.message
  }
  if (typeof message?.text === 'string') {
    return message.text
  }
  return ''
}

function readMessageSenderName(message: any, fallback?: string) {
  const sender = message?.sender
  if (sender?.className === 'User') {
    const name = [sender.firstName, sender.lastName].filter(Boolean).join(' ').trim() || sender.username || sender.phone
    if (name) {
      return name
    }
  }

  if (typeof message?.postAuthor === 'string' && message.postAuthor.trim()) {
    return message.postAuthor.trim()
  }

  return fallback
}

async function extractEntityAvatarUrl(entity: any) {
  const strippedThumb = entity?.photo?.strippedThumb
  if (!strippedThumb) {
    return undefined
  }

  try {
    const { strippedPhotoToJpg } = await loadTelegramRuntime()
    const jpgBytes = strippedPhotoToJpg(Buffer.from(strippedThumb))
    return `data:image/jpeg;base64,${Buffer.from(jpgBytes).toString('base64')}`
  }
  catch {
    return undefined
  }
}

async function downloadEntityAvatarUrl(current: TelegramClientType, entityLike: any) {
  try {
    const downloaded = await current.downloadProfilePhoto(entityLike, {
      isBig: false,
      outputFile: undefined,
    })
    if (!downloaded) {
      return undefined
    }
    return toDataUrl(downloaded as Buffer | Uint8Array | ArrayBuffer)
  }
  catch {
    return undefined
  }
}

async function createClientFromState(state: PersistedSessionState) {
  const { TelegramClient, StringSession, ConnectionTCPObfuscated } = await loadTelegramRuntime()
  const next = new TelegramClient(new StringSession(state.session), state.apiId, state.apiHash, {
    connectionRetries: 5,
    useWSS: true,
    connection: ConnectionTCPObfuscated,
  })
  await next.connect()
  return next
}

async function getAuthorizedClient() {
  if (client) {
    await client.connect()
    if (await client.checkAuthorization()) {
      return client
    }
  }

  const persisted = readPersistedState()
  if (!persisted) {
    throw new Error('尚未登录')
  }

  const next = await createClientFromState(persisted)
  if (!await next.checkAuthorization()) {
    writePersistedState(null)
    throw new Error('Telegram session 已失效，请重新登录')
  }

  client = next
  return next
}

export const telegramBrowser = {
  getConfiguredApiCredentials() {
    const persisted = readPersistedState()
    const apiId = persisted?.apiId || runtimeApiConfig.apiId
    const apiHash = persisted?.apiHash || runtimeApiConfig.apiHash

    return {
      apiId,
      apiHash,
      isConfigured: Boolean(apiId && apiHash),
    }
  },
  async getStatus(): Promise<SessionStatus> {
    await loadRuntimeApiConfig()
    if (pending) {
      return {
        authorized: false,
        pendingStep: pending.step,
        user: null,
        phone: pending.phone,
      }
    }

    try {
      const current = await getAuthorizedClient()
      const me = await current.getMe()
      const persisted = readPersistedState()
      return {
        authorized: true,
        pendingStep: null,
        user: displayUser(me),
        phone: persisted?.phone,
      }
    }
    catch {
      return {
        authorized: false,
        pendingStep: null,
        user: null,
      }
    }
  },
  async startLogin(phone: string) {
    await loadRuntimeApiConfig()
    const credentials = telegramBrowser.getConfiguredApiCredentials()
    if (!credentials.isConfigured) {
      throw new Error('缺少 Telegram API 配置，请先在 .env.local 中设置 VITE_TELEGRAM_API_ID 和 VITE_TELEGRAM_API_HASH')
    }

    const { TelegramClient, StringSession, ConnectionTCPObfuscated } = await loadTelegramRuntime()
    const nextClient = new TelegramClient(new StringSession(''), credentials.apiId, credentials.apiHash, {
      connectionRetries: 5,
      useWSS: true,
      connection: ConnectionTCPObfuscated,
    })
    await nextClient.connect()
    const codeDeferred = createDeferred<string>()
    const passwordDeferred = createDeferred<string>()
    const nextStepDeferred = createDeferred<'password'>()
    const resultDeferred = createDeferred<SessionStatus>()

    pending = {
      client: nextClient,
      apiId: credentials.apiId,
      apiHash: credentials.apiHash,
      phone,
      step: 'code',
      codeDeferred,
      passwordDeferred,
      nextStepDeferred,
      resultDeferred,
    }

    void nextClient.signInUser({
      apiId: credentials.apiId,
      apiHash: credentials.apiHash,
    }, {
      phoneNumber: phone,
      phoneCode: async () => {
        return await codeDeferred.promise
      },
      password: async () => {
        if (pending) {
          pending.step = 'password'
          pending.nextStepDeferred.resolve('password')
        }
        return await passwordDeferred.promise
      },
      onError: async (error) => {
        resultDeferred.reject(error)
        throw error
      },
    }).then(async (user) => {
      const session = nextClient.session.save()
      writePersistedState({
        apiId: credentials.apiId,
        apiHash: credentials.apiHash,
        phone,
        session: typeof session === 'string' ? session : String(session),
      })
      client = nextClient
      pending = null
      resultDeferred.resolve({
        authorized: true,
        pendingStep: null,
        user: displayUser(user as ApiNamespace.User),
        phone,
      })
    }).catch((error) => {
      resultDeferred.reject(error)
    })
  },
  async verifyCode(code: string) {
    if (!pending || pending.step !== 'code') {
      throw new Error('当前没有待验证的验证码')
    }

    try {
      pending.codeDeferred.resolve(code)
      const result = await Promise.race([
        pending.resultDeferred.promise,
        pending.nextStepDeferred.promise,
      ])
      if (result === 'password') {
        return telegramBrowser.getStatus()
      }
      return result
    }
    catch (error: any) {
      throw error
    }
  },
  async verifyPassword(password: string) {
    if (!pending || pending.step !== 'password') {
      throw new Error('当前没有待验证的二步密码')
    }

    pending.passwordDeferred.resolve(password)
    return await pending.resultDeferred.promise
  },
  async logout() {
    try {
      const { Api } = await loadTelegramRuntime()
      const current = await getAuthorizedClient()
      await current.invoke(new Api.auth.LogOut())
      await current.disconnect()
    }
    catch {
      // Ignore logout failures and clear local session anyway.
    }

    client = null
    pending = null
    writePersistedState(null)
  },
  async refreshChats(): Promise<Array<ChatSummary & { peerId: string, accessHash?: string }>> {
    const current = await getAuthorizedClient()
    const dialogs = await current.getDialogs({ limit: 1000 })

    return (await Promise.all(dialogs.map(async (dialog) => {
      const meta = await extractPeerMeta(dialog.inputEntity)
      if (!meta) {
        return null
      }

      return {
        id: buildChatId(meta.peerType, meta.peerId),
        title: dialog.title || dialog.name || 'Untitled Chat',
        peerType: meta.peerType,
        peerId: meta.peerId,
        accessHash: meta.accessHash,
        avatarUrl: await extractEntityAvatarUrl(dialog.entity),
        username: (dialog.entity as any)?.username || undefined,
        lastMessageAt: dialog.date ? Number(dialog.date) * 1000 : undefined,
        syncedMessageCount: 0,
        syncedStorageBytes: 0,
      }
    }))).filter(Boolean) as Array<ChatSummary & { peerId: string, accessHash?: string }>
  },
  async loadChatAvatar(chat: ChatSummary & { peerId?: string, accessHash?: string }) {
    if (!chat.peerId) {
      return undefined
    }

    const current = await getAuthorizedClient()
    const peer = await makeInputPeer(chat as ChatSummary & { peerId: string, accessHash?: string })
    const entity = await current.getEntity(peer)
    return await downloadEntityAvatarUrl(current, entity) ?? await extractEntityAvatarUrl(entity)
  },
  async searchMessagesBySender(
    chats: Array<ChatSummary & { peerId?: string, accessHash?: string }>,
    query: string,
    onProgress: (task: UserMessageSearchStatus) => void,
    onBatch?: (messages: SearchResultRecord[]) => Promise<void> | void,
  ): Promise<{
    task: UserMessageSearchStatus
    results: SearchResultRecord[]
    resolvedSenderId: string
    resolvedSenderName: string
  }> {
    const current = await getAuthorizedClient()
    const searchableChats = chats.filter((chat) => (
      Boolean(chat.peerId)
      && (chat.peerType === 'chat' || chat.peerType === 'channel')
    )) as Array<ChatSummary & { peerId: string, accessHash?: string }>
    const task: UserMessageSearchStatus = {
      id: `user-search:${Date.now()}`,
      mode: 'remote',
      query,
      status: 'running',
      scannedChats: 0,
      totalChats: searchableChats.length,
      matchedMessages: 0,
      startedAt: Date.now(),
      note: '正在逐个群聊调用 Telegram 的发送者筛选接口。',
    }
    const results: SearchResultRecord[] = []
    let pendingBatch: SearchResultRecord[] = []
    const seenMessageKeys = new Set<string>()
    let resolvedSender: ResolvedUserLookup | null = null
    let skippedChats = 0
    const searchCancellation: SyncCancellationState = {
      taskId: task.id,
      cancelled: false,
    }
    activeUserSearchCancellation = searchCancellation
    const assertSearchNotCancelled = () => {
      if (searchCancellation.cancelled) {
        throw buildUserSearchCancelledError()
      }
    }
    const flushBatch = async () => {
      if (!onBatch || pendingBatch.length === 0) {
        return
      }

      const batch = pendingBatch
      pendingBatch = []
      await onBatch(batch)
    }

    onProgress({ ...task })

    try {
      resolvedSender = await resolveUserLookup(current, query)
      task.resolvedSenderId = resolvedSender.senderId
      task.resolvedSenderName = resolvedSender.displayName
      onProgress({ ...task })

      for (const chat of searchableChats) {
        assertSearchNotCancelled()

        try {
          const peer = await makeInputPeer(chat)
          for await (const message of current.iterMessages(peer, {
            fromUser: resolvedSender.inputUser,
            limit: undefined,
            search: '',
            waitTime: 0,
          })) {
            assertSearchNotCancelled()
            const messageKey = `${chat.id}:${Number(message.id)}`
            if (seenMessageKeys.has(messageKey)) {
              continue
            }

            seenMessageKeys.add(messageKey)
            const nextMessage = {
              chatId: chat.id,
              chatTitle: chat.title,
              messageId: Number(message.id),
              senderId: resolvedSender.senderId,
              senderName: readMessageSenderName(message, resolvedSender.displayName),
              text: readMessageText(message),
              dateTs: getMessageDateTs(message),
              replyToMessageId: message.replyTo?.replyToMsgId ? Number(message.replyTo.replyToMsgId) : undefined,
            }
            results.push(nextMessage)
            pendingBatch.push(nextMessage)

            if (pendingBatch.length >= 24) {
              await flushBatch()
            }
          }
          await flushBatch()
        }
        catch (error) {
          if ((error as Error & { code?: string })?.code === 'USER_SEARCH_CANCELLED') {
            throw error
          }
          skippedChats += 1
        }

        task.scannedChats += 1
        task.matchedMessages = results.length
        task.note = skippedChats > 0
          ? `已跳过 ${skippedChats} 个当前无法检索的会话。`
          : '正在逐个群聊调用 Telegram 的发送者筛选接口。'
        onProgress({ ...task })
      }

      task.status = 'done'
      task.finishedAt = Date.now()
      task.matchedMessages = results.length
      task.note = skippedChats > 0
        ? `已完成，期间跳过了 ${skippedChats} 个当前无法检索的会话。`
        : '已完成全部群聊筛选。'
      await flushBatch()
      onProgress({ ...task })

      return {
        task,
        results: results.sort((a, b) => b.dateTs - a.dateTs || b.messageId - a.messageId),
        resolvedSenderId: resolvedSender.senderId,
        resolvedSenderName: resolvedSender.displayName,
      }
    }
    catch (error: any) {
      task.status = error?.code === 'USER_SEARCH_CANCELLED' ? 'cancelled' : 'error'
      task.finishedAt = Date.now()
      task.error = error?.errorMessage || error?.message || (task.status === 'cancelled' ? '已取消按用户筛选' : '按用户筛选失败')
      onProgress({ ...task })
      throw error
    }
    finally {
      if (activeUserSearchCancellation?.taskId === task.id) {
        activeUserSearchCancellation = null
      }
    }
  },
  async syncChatHistory(
    chat: ChatSummary & { peerId: string, accessHash?: string },
    rangeStartTs: number,
    rangeEndTs: number,
    onProgress: (task: SyncTaskStatus) => void,
  ): Promise<{ task: SyncTaskStatus, messages: MessageRecord[] }> {
    const { Api } = await loadTelegramRuntime()
    const current = await getAuthorizedClient()
    const task: SyncTaskStatus = {
      id: `${chat.id}:${Date.now()}`,
      chatId: chat.id,
      status: 'running',
      processed: 0,
      saved: 0,
      startedAt: Date.now(),
      rangeStartTs,
      rangeEndTs,
    }

    let takeout: Api.account.TypeTakeout | null = null
    const messages: MessageRecord[] = []
    const syncCancellation: SyncCancellationState = {
      taskId: task.id,
      cancelled: false,
    }
    activeSyncCancellation = syncCancellation
    const assertSyncNotCancelled = () => {
      if (syncCancellation.cancelled) {
        throw buildSyncCancelledError()
      }
    }
    onProgress({ ...task })

    try {
      assertSyncNotCancelled()
      takeout = await current.invoke(new Api.account.InitTakeoutSession({
        contacts: true,
        messageUsers: true,
        messageChats: true,
        messageMegagroups: true,
        messageChannels: true,
        files: false,
      }))

      assertSyncNotCancelled()
      const peer = await makeInputPeer(chat)
      const splitRanges = await current.invoke(new Api.messages.GetSplitRanges()) as any[]
      const ranges = splitRanges.length > 0 ? splitRanges : [null]
      let fallbackToRegularHistory = false

      for (const range of ranges) {
        assertSyncNotCancelled()
        let offsetId = 0
        let hasMore = true

        while (hasMore) {
          assertSyncNotCancelled()
          const historyQuery = new Api.messages.GetHistory({
            peer,
            offsetId,
            offsetDate: 0,
            addOffset: 0,
            limit: 100,
            maxId: 0,
            minId: 0,
            hash: BigInt(0) as any,
          })

          let query: any = historyQuery
          if (range && !fallbackToRegularHistory) {
            query = new Api.InvokeWithMessagesRange({
              range,
              query,
            })
          }

          let historyResult: any
          if (fallbackToRegularHistory) {
            historyResult = await current.invoke(historyQuery) as any
          }
          else {
            historyResult = await current.invoke(new Api.InvokeWithTakeout({
              takeoutId: (takeout as any).id,
              query,
            })) as any
          }
          assertSyncNotCancelled()

          if (!fallbackToRegularHistory && (historyResult?.messages?.length ?? 0) === 0) {
            const fallbackResult = await current.invoke(historyQuery) as any
            assertSyncNotCancelled()

            if ((fallbackResult?.messages?.length ?? 0) > 0) {
              // In browser-only mode, Telegram may return an empty takeout history page
              // for some chats even though regular GetHistory works for the same peer.
              fallbackToRegularHistory = true
              historyResult = fallbackResult
            }
          }

          const entityMap = new Map<string, string>()
          for (const user of historyResult.users ?? []) {
            const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim() || user.username || user.phone || ''
            entityMap.set(`user:${user.id.toString()}`, name)
          }
          for (const item of historyResult.chats ?? []) {
            const type = item.className === 'Channel' ? 'channel' : 'chat'
            entityMap.set(`${type}:${item.id.toString()}`, item.title || '')
          }

          const batch = (historyResult.messages ?? []).filter((message: any) => isHistoryMessage(message))
          if (batch.length === 0) {
            break
          }

          for (const message of batch) {
            assertSyncNotCancelled()
            const dateTs = getMessageDateTs(message)
            task.processed += 1
            task.lastMessageTs = dateTs

            if (dateTs < rangeStartTs) {
              hasMore = false
              continue
            }
            if (dateTs > rangeEndTs) {
              continue
            }

            const senderId = peerKey(message.fromId)
            messages.push({
              chatId: chat.id,
              messageId: Number(message.id),
              senderId,
              senderName: (senderId ? entityMap.get(senderId) : undefined) || message.postAuthor || undefined,
              text: typeof message.message === 'string' ? message.message : '',
              dateTs,
              replyToMessageId: message.replyTo?.replyToMsgId ? Number(message.replyTo.replyToMsgId) : undefined,
            })
          }

          task.saved = messages.length
          onProgress({ ...task })
          offsetId = Number(batch[batch.length - 1].id)
          hasMore = hasMore && batch.length === 100
        }
      }

      task.status = 'done'
      task.finishedAt = Date.now()
      task.saved = messages.length
      onProgress({ ...task })

      return { task, messages }
    }
    catch (error: any) {
      const normalizedError = normalizeSyncError(error)
      task.status = normalizedError?.code === 'SYNC_CANCELLED' ? 'cancelled' : 'error'
      task.finishedAt = Date.now()
      task.error = normalizedError?.message || normalizedError?.errorMessage || (task.status === 'cancelled' ? '已取消同步' : '同步失败')
      onProgress({ ...task })
      throw normalizedError
    }
    finally {
      if (activeSyncCancellation?.taskId === task.id) {
        activeSyncCancellation = null
      }
      if (takeout && 'id' in takeout) {
        try {
          await current.invoke(new Api.InvokeWithTakeout({
            takeoutId: (takeout as any).id,
            query: new Api.account.FinishTakeoutSession({
              success: task.status === 'done',
            }),
          }))
        }
        catch {
          // Ignore cleanup errors.
        }
      }
    }
  },
  cancelSync() {
    if (!activeSyncCancellation) {
      return false
    }

    activeSyncCancellation.cancelled = true
    return true
  },
  cancelUserMessageSearch() {
    if (!activeUserSearchCancellation) {
      return false
    }

    activeUserSearchCancellation.cancelled = true
    return true
  },
}
